import type { LocationQuery } from 'vue-router';
import { CustomFilters, Facets, SortType } from '~/components/Product/types/product';
import { replaceHistory } from '~/helpers/utils/replaceHistory';
import { useBFF } from '~/composables/useBFF/useBFF';

type FacetsType = Record<Facets, string>;
export interface ProductFacets extends Omit<FacetsType, 'search_color' | 'price'> {
  price?: {
    min: number;
    max: number;
  };
  search_color: {
    options: {
      id: string;
      code: string;
      label: string;
    }[];
    id: string;
  };
  hide?: {
    sale: boolean;
    recent: boolean;
    mykik: boolean;
    oekotex: boolean;
  };
}

export interface SelectedFilters extends Omit<FacetsType, 'search_color' | 'price' | 'category_path'> {
  price?: { min: number; max: number };
  search_color: string[];
  recent: boolean;
  sale: boolean;
  sort?: string;
  mykik?: boolean;
  targetgroup?: string;
  oekotex?: boolean;
  search?: string;
  category_path?: string;
}

export const useFilters = () => {
  const route = useRoute();
  const { $products } = useBFF();
  const { $i18n } = useNuxtApp();

  const {
    slug_1: slug1,
    slug_2: slug2,
    slug_3: slug3,
    slug_4: slug4,
  } = route.params;

  // Die ID soll auch die Suchanfrage berücksichtigen, falls vorhanden
  const searchQuery = useRoute().query.search as string | undefined;
  const idParts = [slug1, slug2, slug3, slug4].filter(item => !item?.includes('c_')).filter(Boolean);
  if (searchQuery) {
    idParts.push(`search-${searchQuery}`);
  }
  const id = idParts.join('-');

  const filterList: Ref<ProductFacets> = useState(`list-of-facets-${id}`);
  const categoryFacets: Ref<any> = useState(`facets-${id}`, () => undefined);
  const priceFilter: Ref<any> = useState(`price-filter-${id}`, () => undefined);
  const selectedFiltersTransformed: Ref<any> = useState(`selected-filters-transformed-${id}`, () => []);
  const selectedFilters: Ref<SelectedFilters> = useState(`selected-filters-${id}`, undefined);
  const headlineColorSelected: Ref<any> = useState(`headline-color-selected-${id}`, () => '');

  const saleParam = $i18n.t('Sale').toString().toLowerCase();
  const newParam = $i18n.t('New').toString().toLowerCase();
  const mykikParam = $i18n.t('mykik').toString().toLowerCase();
  const oekotexParam = $i18n.t('Oekotex').toString().toLowerCase();

  const getColorTranslated = (colors: any) => filterList.value?.search_color?.options?.length
    ? colors.split(',').map((color: string) => {
        const id = filterList.value.search_color.options.find(({ label }) => label === color)?.id;
        return id ? $i18n.t(id) : color;
      }).join(', ')
    : colors;

  const setFilters = (filters: ProductFacets) => {
    filterList.value = filters;
    transformFilters();
  };

  const getCurrentFilters = () => {
    let filters = Object.keys(route.query).filter(key => Object.values<string>(Facets).includes(key) || Object.values<string>(CustomFilters).includes(key)).reduce((obj, key) => {
      obj[key] = route.query[key];
      return obj;
    }, {} as LocationQuery);

    if (route.path.includes('c_')) {
      const colors = route.path.split('c_')[1].split('.');
      // eslint-disable-next-line camelcase
      filters = { ...filters, search_color: colors.join(',') };
    }
    if (import.meta.client) {
      const queryParams = new URLSearchParams(window.location.search);
      headlineColorSelected.value = '';

      if (window.location.pathname.includes('c_')) {
        const colors = window.location.pathname.split('c_')[1].split('.');
        headlineColorSelected.value = colors.length === 1 ? getColorTranslated(colors[0]) : '';
        queryParams.set('search_color', colors.join(','));
      }

      filters = Object.keys(Object.fromEntries(queryParams)).filter(key => Object.values<string>(Facets).includes(key) || Object.values<string>(CustomFilters).includes(key)).reduce((obj, key) => {
        obj[key] = Object.fromEntries(queryParams)[key];
        return obj;
      }, {} as LocationQuery);
    }

    return filters;
  };

  const pushPriceRangeMin = (filters: URLSearchParams | LocationQuery) => {
    if (filters instanceof URLSearchParams) {
      selectedFiltersTransformed.value.push({ key: Facets.PRICE, value: `${$i18n.n(Number(filters.get(CustomFilters.PRICE_RANGE_MIN)), 'currency')} - ${$i18n.n(Number(filters.get(CustomFilters.PRICE_RANGE_MAX)), 'currency')}` });

      return;
    }
    selectedFiltersTransformed.value.push({ key: Facets.PRICE, value: `${$i18n.n(Number(filters[CustomFilters.PRICE_RANGE_MIN]), 'currency')} - ${$i18n.n(Number(filters[CustomFilters.PRICE_RANGE_MAX]), 'currency')}` });
  };

  const transformFilters = (params?: URLSearchParams) => {
    const filters = params ? params : getCurrentFilters();
    selectedFiltersTransformed.value = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (key === CustomFilters.SALE_AND_NEW) {
        (value)?.split(',').forEach((filter: ProductFacets) => {
          selectedFiltersTransformed.value.push({ key: filter, value: filter });
        });

        return;
      }
      if (key === CustomFilters.TARGET_GROUP) {
        selectedFiltersTransformed.value.push({
          key,
          value: $i18n.t(`target_group_${value}`),
        });

        return;
      }
      if (key === CustomFilters.PRICE_RANGE_MIN) {
        pushPriceRangeMin(filters);

        return;
      }
      if (key === CustomFilters.SEARCH_COLOR) {
        selectedFiltersTransformed.value.push({ key, value: getColorTranslated(value) });

        return;
      }
      if (key !== CustomFilters.PRICE_RANGE_MAX
        && key !== CustomFilters.SEARCH
        && key !== CustomFilters.SORT
        && key !== 'page'
        && key !== 'redirectTo') {
        selectedFiltersTransformed.value.push({ key, value });

        return;
      }
    });
  };

  const getFilters = (originalAttributes = {}) => {
    const filters = getCurrentFilters();
    const facetsValues: string[] = Object.values(Facets);
    const newAndSale = (filters?.[CustomFilters.SALE_AND_NEW] as string)?.split(',') || [];
    const categoryPath = (filters?.[CustomFilters.CATEGORY_PATH] as string)?.split(',');

    const result = Object.keys(filters || {}).reduce((acc, currentValue) => {
      if (facetsValues.includes(currentValue)) {
        return {
          ...acc,
          [currentValue]: (filters[currentValue] as string).split(','),
        };
      }
      return acc;
    }, {
      sale: newAndSale.includes(saleParam) ?? false,
      recent: newAndSale.includes(newParam) ?? false,
      mykik: newAndSale.includes(mykikParam) ?? false,
      oekotex: newAndSale.includes(oekotexParam) ?? false,
      sort: filters?.[CustomFilters.SORT],
      search: filters?.[CustomFilters.SEARCH] ?? '',
      targetgroup: filters?.[CustomFilters.TARGET_GROUP] ?? '',
      price: filters?.[CustomFilters.PRICE_RANGE_MIN] && filters?.[CustomFilters.PRICE_RANGE_MAX] ? { min: Number(filters[CustomFilters.PRICE_RANGE_MIN]), max: Number(filters[CustomFilters.PRICE_RANGE_MAX]) } : undefined,
      // eslint-disable-next-line camelcase
      category_path: categoryPath ?? undefined,
    });

    return {
      ...result,
      ...originalAttributes,
    };
  };

  const getSearchParamsWithoutPreviousFilters = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParamName: string[] = Object.values(Facets);
    const customQueryParams = Object.values(CustomFilters);

    [...queryParamName, ...customQueryParams].forEach((value) => {
      if (value !== CustomFilters.SEARCH) {
        searchParams.delete(value);
      }
    });

    return searchParams;
  };

  const resetFilters = async (categories?: string[], products?: string[]) => {
    const searchParams = getSearchParamsWithoutPreviousFilters();

    replaceHistory(route.path, searchParams);
    await calculateFilters(categories, products, true);
  };

  const addFiltersToPath = (params: any) => {
    const searchParams = new URLSearchParams(window.location.search);
    Object.keys(params).forEach((key) => {
      searchParams.set(key, params[key]);
    });
    replaceHistory(route.path, searchParams);
  };

  const changeFilters = async (selectedFilters: SelectedFilters) => {
    const {
      sale, recent, mykik, sort, price, search, targetgroup, oekotex, category_path: categoryPath, search_color: searchColor, ...filters
    } = selectedFilters;

    let path = route.path;

    const filterKeys = Object.keys(filters || {});
    const searchParams = getSearchParamsWithoutPreviousFilters();

    if (recent || sale || mykik || oekotex) {
      const params = [
        recent ? newParam : undefined,
        sale ? saleParam : undefined,
        mykik ? mykikParam : undefined,
        oekotex ? oekotexParam : undefined]
        .filter(Boolean)
        .join(',');
      searchParams.set(CustomFilters.SALE_AND_NEW, params);
    }

    if (search) {
      searchParams.set(CustomFilters.SEARCH, search);
    }

    if (categoryPath) {
      searchParams.set(CustomFilters.CATEGORY_PATH, categoryPath);
    }

    if (sort) {
      searchParams.set(CustomFilters.SORT, sort);
    }

    if (targetgroup) {
      searchParams.set(CustomFilters.TARGET_GROUP, targetgroup);
    }

    if (price?.min !== undefined && price?.max && (priceFilter.value?.min !== price.min || priceFilter.value?.max !== price.max)) {
      searchParams.set(CustomFilters.PRICE_RANGE_MIN, price.min.toString());
      searchParams.set(CustomFilters.PRICE_RANGE_MAX, price.max.toString());
    }

    filterKeys.forEach((key: string) => {
      const value = filters[key as keyof typeof filters];
      if (value?.length) {
        searchParams.set(key, `${value}`);
      }
    });

    transformFilters(searchParams);

    if (searchColor?.length) {
      const colors = searchColor.join('.');
      headlineColorSelected.value = getColorTranslated(colors);
      path = path.includes('c_') ? path.replace(/c_[^/]+/, `c_${colors}`) : `${path}/c_${colors}`;
    }
    else {
      headlineColorSelected.value = '';
      path = path.includes('c_') ? path.replace(/\/c_[^/]+/, '') : path;
    }
    replaceHistory(path, searchParams);
  };

  const getInitialPriceRange = async (categories?: string[], products?: any[], reset?: boolean): Promise<{ min: number; max: number }> => {
    if ((!categoryFacets.value && categories?.length) || (reset && categories?.length)) {
      await getCategoryFacets([Facets.PRICE], categories, products, route.meta?.isSale ? { sale: route.meta?.isSale } : undefined);
    }
    return {
      min: categoryFacets.value?.price?.min ?? 0,
      max: categoryFacets.value?.price?.max ?? 100,
    };
  };

  const applyInitialPriceRange = async (categories?: string[], products?: any[]) => {
    const initialObject = getFilters();
    priceFilter.value = await getInitialPriceRange(categories, products, true);
    const initialFilters = Object.values(Facets)?.reduce((acc, curr) => ({ ...acc, [curr]: initialObject?.[curr as keyof typeof initialObject] ?? [] }), {});
    selectedFilters.value = {
      ...initialObject,
      ...initialFilters,
      price: priceFilter.value,
    } as any;
  };

  const calculateFilters = async (categories?: string[], products?: string[], removeColor?: boolean) => {
    const initialObject = getFilters();
    priceFilter.value = await getInitialPriceRange(categories, products);
    const initialFilters = Object.values(Facets)?.reduce((acc, curr) => ({ ...acc, [curr]: initialObject?.[curr as keyof typeof initialObject] ?? [] }), {});
    selectedFilters.value = {
      ...initialObject,
      ...initialFilters,
      price: initialObject?.price ?? priceFilter.value,
      // eslint-disable-next-line camelcase
      ...(removeColor && { search_color: [] as string[] }),
    } as any;
  };

  const getCategoryFacets = async (facetsFilters: Facets[], categories: string[], products?: any[], filterAndSortBy?: any) => {
    const { data } = await $products.getCategoryFacets({
      categories,
      facetsFilters,
      products,
      filterAndSortBy,
    });
    categoryFacets.value = (data as { data: unknown }).data;
  };

  const setSelectedFilter = (key: string, value: any) => {
    selectedFilters.value = {
      ...selectedFilters.value,
      [key]: value,
    };
  };

  const applyFilter = async (facetId: string, option: any) => {
    setSelectedFilter(facetId, option);
    await changeFilters(selectedFilters.value);
  };

  const removeFilters = async (categories?: string[], products?: any[]) => {
    await resetFilters(categories, products);
    await changeFilters(selectedFilters.value);
  };

  const removeFilter = async (facetId: string, categories: string[], products: any[]) => {
    if (facetId === Facets.PRICE) {
      priceFilter.value = await getInitialPriceRange(categories, products);
      await applyFilter(Facets.PRICE, priceFilter.value);
    }
    else {
      let newSelectedList = selectedFilters.value[facetId as keyof SelectedFilters];

      if (Array.isArray(newSelectedList)) {
        newSelectedList = [];
      }
      else if (typeof newSelectedList === 'string') {
        newSelectedList = '';
      }
      else if (typeof newSelectedList === 'boolean') {
        newSelectedList = false;
      }

      await applyFilter(facetId, newSelectedList);
    }
  };

  const getSelectedFilterValue = (facetId: string) => {
    if (selectedFiltersTransformed.value.length) {
      return selectedFiltersTransformed.value.find((filter: { key: string; value: string }) => filter.key === facetId)?.value;
    }

    return null;
  };

  return {
    priceFilter: computed(() => priceFilter.value),
    selectedFiltersTransformed: computed(() => selectedFiltersTransformed.value),
    selectedFilters: computed(() => selectedFilters.value),
    sortOptions: computed(() => Object.values(SortType).map(value => ({ label: value, value }))),
    headlineColorSelected: computed(() => headlineColorSelected.value),
    filterList: computed(() => filterList.value),
    setFilters,
    setSelectedFilter,
    applyFilter,
    removeFilter,
    removeFilters,
    getFilters,
    changeFilters,
    calculateFilters,
    getInitialPriceRange,
    applyInitialPriceRange,
    resetFilters,
    addFiltersToPath,
    getSelectedFilterValue,
  };
};
